import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import {
  BUDGET_RANGES, COMPANY, FEATURES_INCLUDED, PACKAGES, PROJECT_TYPES,
} from "../lib/constants";
import { submitLead } from "../lib/api";

export default function Quotation() {
  const [params] = useSearchParams();
  const initialPkg = (params.get("package") || "").toLowerCase();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    whatsapp: "",
    email: "",
    plot_size: "",
    location: "",
    project_type: "",
    budget: "",
    package: PACKAGES.find((p) => p.key === initialPkg)?.name || "",
    message: "",
    source: "quotation",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid = useMemo(() => form.name.trim() && form.phone.trim().length >= 8, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please share at least your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Quotation request received. We'll call you within 24 hours.");
      // Open WhatsApp pre-filled
      const wa = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
        `Hello Agartha, I just submitted a quotation request.\n\nName: ${form.name}\nPhone: ${form.phone}\nLocation: ${form.location}\nPlot Size: ${form.plot_size}\nProject: ${form.project_type}\nPackage: ${form.package}\nBudget: ${form.budget}`
      )}`;
      setTimeout(() => window.open(wa, "_blank"), 800);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="quotation-page">
      <section className="bg-[var(--m-bg-2)] section-pad pt-32 md:pt-44 pb-16">
        <div className="container-x grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <span className="eyebrow">Free Quotation</span>
            <div className="gold-divider mt-4" />
            <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1] tracking-tight">
              A line-item estimate.<br />
              <em className="italic text-[var(--m-teal)]">In 24 hours.</em>
            </h1>
            <p className="mt-8 text-[var(--m-ink-2)] text-lg max-w-xl leading-relaxed">
              Tell us about your plot. We'll send a transparent quotation with exact material brands,
              per-bag and per-tonne rates, and a realistic timeline.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="border border-[var(--m-border)] bg-[var(--m-bg)] p-8">
              <h3 className="font-display text-xl mb-5">Every quotation includes</h3>
              <ul className="grid grid-cols-2 gap-y-3 gap-x-4">
                {FEATURES_INCLUDED.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-[var(--m-ink-2)]">
                    <CheckCircle2 size={13} className="text-[var(--m-teal)] mt-0.5" strokeWidth={1.6} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGE COMPARISON */}
      <section className="section-pad pt-0" data-testid="quotation-packages">
        <div className="container-x">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl mb-10">Choose a starting package</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.key} delay={i * 80}>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, package: p.name }))}
                  className="text-left p-8 border bg-[var(--m-bg)] w-full transition-all"
                  style={{
                    borderColor: form.package === p.name ? "var(--m-teal)" : "var(--m-border)",
                    boxShadow: form.package === p.name ? "0 16px 32px -16px rgba(31,78,91,0.4)" : "none",
                  }}
                  data-testid={`quote-pkg-${p.key}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs tracking-[0.22em] uppercase text-[var(--m-ink-3)]">{p.name}</span>
                    {form.package === p.name && <CheckCircle2 size={18} className="text-[var(--m-teal)]" />}
                  </div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-display text-4xl">₹{p.price}</span>
                    <span className="text-xs text-[var(--m-ink-3)]">/sq.ft.</span>
                  </div>
                  <p className="mt-3 text-xs text-[var(--m-ink-2)]">{p.features[0]}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-[var(--m-ink)] text-white section-pad" data-testid="quotation-form-section">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-[var(--m-gold)] text-xs tracking-[0.32em] uppercase">Smart Inquiry Form</span>
            <h2 className="font-display text-4xl md:text-5xl mt-5 leading-[1.05]">
              Tell us about your build.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              Your details are kept confidential and used only to prepare your quotation.
            </p>
            <div className="mt-10 flex flex-col gap-3 text-sm text-white/80">
              <div>📞 {COMPANY.phone}</div>
              <div>✉️ {COMPANY.email}</div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
            data-testid="quotation-form"
          >
            {done && (
              <div className="md:col-span-2 border border-[var(--m-gold)] bg-white/5 p-6 flex items-start gap-4" data-testid="quote-success">
                <CheckCircle2 size={22} className="text-[var(--m-gold)] mt-1" />
                <div>
                  <div className="font-display text-xl">Request received.</div>
                  <p className="text-sm text-white/80 mt-1">
                    Mr. Kiran or our team will call you within 24 hours. We've also opened WhatsApp so
                    you can share photos of your plot if you wish.
                  </p>
                </div>
              </div>
            )}

            <Field label="Full Name *">
              <input className="input-line text-white placeholder:text-white/40" style={{ borderBottomColor: "rgba(255,255,255,0.2)" }} required value={form.name} onChange={set("name")} placeholder="e.g. Anitha Reddy" data-testid="quote-name" />
            </Field>
            <Field label="Phone Number *">
              <input className="input-line text-white placeholder:text-white/40" style={{ borderBottomColor: "rgba(255,255,255,0.2)" }} required value={form.phone} onChange={set("phone")} placeholder="+91 9XXXX XXXXX" data-testid="quote-phone" />
            </Field>
            <Field label="WhatsApp (if different)">
              <input className="input-line text-white placeholder:text-white/40" style={{ borderBottomColor: "rgba(255,255,255,0.2)" }} value={form.whatsapp} onChange={set("whatsapp")} placeholder="+91 9XXXX XXXXX" data-testid="quote-whatsapp" />
            </Field>
            <Field label="Email">
              <input className="input-line text-white placeholder:text-white/40" style={{ borderBottomColor: "rgba(255,255,255,0.2)" }} type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="quote-email" />
            </Field>
            <Field label="Plot Size">
              <input className="input-line text-white placeholder:text-white/40" style={{ borderBottomColor: "rgba(255,255,255,0.2)" }} value={form.plot_size} onChange={set("plot_size")} placeholder="e.g. 30 × 40 ft / 1200 sq.ft." data-testid="quote-plotsize" />
            </Field>
            <Field label="Location">
              <input className="input-line text-white placeholder:text-white/40" style={{ borderBottomColor: "rgba(255,255,255,0.2)" }} value={form.location} onChange={set("location")} placeholder="Area, Bengaluru" data-testid="quote-location" />
            </Field>
            <Field label="Project Type">
              <select className="input-line text-white" style={{ borderBottomColor: "rgba(255,255,255,0.2)", backgroundColor: "var(--m-ink)" }} value={form.project_type} onChange={set("project_type")} data-testid="quote-type">
                <option value="">— Select —</option>
                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Budget Range">
              <select className="input-line text-white" style={{ borderBottomColor: "rgba(255,255,255,0.2)", backgroundColor: "var(--m-ink)" }} value={form.budget} onChange={set("budget")} data-testid="quote-budget">
                <option value="">— Select —</option>
                {BUDGET_RANGES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Preferred Package">
              <select className="input-line text-white" style={{ borderBottomColor: "rgba(255,255,255,0.2)", backgroundColor: "var(--m-ink)" }} value={form.package} onChange={set("package")} data-testid="quote-package">
                <option value="">— Choose —</option>
                {PACKAGES.map((p) => <option key={p.key} value={p.name}>{`${p.name} — ₹${p.price}/sq.ft.`}</option>)}
                <option value="Custom">Custom / Not Sure</option>
              </select>
            </Field>
            <Field label="Anything else?" className="md:col-span-2">
              <textarea className="input-line text-white placeholder:text-white/40 resize-none" style={{ borderBottomColor: "rgba(255,255,255,0.2)" }} rows={3} value={form.message} onChange={set("message")} placeholder="Timeline, special requirements, custom designs..." data-testid="quote-message" />
            </Field>

            <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-4">
              <button
                type="submit"
                disabled={submitting || !valid}
                className="btn-gold disabled:opacity-50"
                data-testid="quote-submit"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={15} />}
                {submitting ? "Submitting..." : "Submit Quotation Request"}
              </button>
              <p className="text-xs text-white/50">By submitting, you agree to be contacted by Agartha Construction.</p>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[10px] tracking-[0.22em] uppercase text-white/50 font-medium block mb-1">{label}</label>
    {children}
  </div>
);
